import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, IconButton, List, ListItem, ListItemText, Tooltip, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { deleteAttachment, downloadAttachment, listAttachments, uploadAttachment } from '../../../api/leads';
import { downloadBlob } from '../../../utils/downloadBlob';

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const AttachmentsTab = ({ leadId }: { leadId: number }) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const attachmentsQuery = useQuery({
    queryKey: ['attachments', leadId],
    queryFn: () => listAttachments(leadId),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadAttachment(leadId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attachments', leadId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: number) => deleteAttachment(attachmentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attachments', leadId] }),
  });

  const handleDownload = async (attachmentId: number) => {
    const { blob, filename } = await downloadAttachment(attachmentId);
    downloadBlob(blob, filename);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) uploadMutation.mutate(file);
          e.target.value = '';
        }}
      />
      <Button
        variant="outlined"
        startIcon={<UploadFileIcon />}
        disabled={uploadMutation.isPending}
        onClick={() => fileInputRef.current?.click()}
        sx={{ mb: 2 }}
      >
        {uploadMutation.isPending ? 'Uploading…' : 'Upload attachment'}
      </Button>

      <List>
        {attachmentsQuery.data?.map((a) => (
          <ListItem
            key={a.id}
            divider
            secondaryAction={
              <>
                <Tooltip title="Download">
                  <IconButton onClick={() => handleDownload(a.id)}>
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton onClick={() => deleteMutation.mutate(a.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            }
          >
            <ListItemText
              primary={a.originalName}
              secondary={`${formatSize(a.sizeBytes)} · uploaded by ${a.uploadedBy.firstName} ${a.uploadedBy.lastName} on ${new Date(a.createdAt).toLocaleDateString()}`}
            />
          </ListItem>
        ))}
      </List>

      {attachmentsQuery.data?.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No attachments yet.
        </Typography>
      )}
    </Box>
  );
};
